import { AnnotationDotsIcon } from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';

import { useState } from 'react';

import Banner from '@/components/Banner';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import useToast from '@/components/Toast/useToast';
import { Button, Input, Text, Textarea } from '@/design-system';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  useChangePaymentPlanMutation,
  useCurrentOrganization,
  useCurrentOrganizationBilling,
  useUser,
} from '@/hooks/useSwr';

import {
  FormFieldIds,
  useSubmitSubscriptionCancellationForm,
} from '../hooks/useSubmitSubscriptionCancellationForm';
import {
  DEFAULT_CANCEL_PLAN_MESSAGE,
  REMOVE_PAYMENT_METHOD_MESSAGE,
} from '../utils/constants';
import { getDisplayDateFromEndOfBillingPeriod } from '../utils/helpers';

enum ReasonsForUnsubscribing {
  Expensive = 'Too expensive',
  Complex = 'Too complex or difficult to use',
  Features = 'Missing features I need',
  OtherTools = 'Using other tools that cover my needs',
  Evaluating = 'Still evaluating other tools',
  Other = 'Other',
}

const CancelConfirmationStep = ({
  isDeveloper,
  lastDayOfBillingPeriodFormatted,
  onClose,
  handleContinue,
}: {
  isDeveloper: boolean;
  lastDayOfBillingPeriodFormatted: string | undefined;
  onClose: () => void;
  handleContinue: () => void;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text variant="body">
          {isDeveloper
            ? REMOVE_PAYMENT_METHOD_MESSAGE[0]
            : DEFAULT_CANCEL_PLAN_MESSAGE[0]}{' '}
          <span className="text-bold">{lastDayOfBillingPeriodFormatted}</span>.
        </Text>
        <Banner
          title={
            isDeveloper
              ? REMOVE_PAYMENT_METHOD_MESSAGE[1]
              : DEFAULT_CANCEL_PLAN_MESSAGE[1]
          }
          open={true}
          shadow={false}
        />
      </div>
      <div className="mt-4 flex flex-row justify-end gap-2">
        <Button
          color="secondary"
          variant="outlined"
          onClick={onClose}
          size="md"
        >
          {isDeveloper ? 'Keep payment method' : 'Keep subscription'}
        </Button>
        <Button color="primary" onClick={handleContinue} size="md">
          Continue
        </Button>
      </div>
    </div>
  );
};

const CancelFormStep = ({
  isDeveloper,
  onClose,
}: {
  isDeveloper: boolean;
  onClose: () => void;
}) => {
  const { createToast } = useToast();
  // Get current user's email
  const { session } = useAuth();
  const { data: user } = useUser();
  const userEmail = user?.data?.user?.email || session?.user?.email || '';
  const { data: currentOrg } = useCurrentOrganization({
    revalidateOnFocus: false,
  });

  const [reasonsForUnsubscribing, setReasonsForUnsubscribing] = useState<
    Set<ReasonsForUnsubscribing>
  >(new Set());
  const [otherReason, setOtherReason] = useState<string>('');
  const [extraDetails, setExtraDetails] = useState<string>('');

  const { trigger, isMutating: isSubmittingForm } =
    useSubmitSubscriptionCancellationForm();
  const {
    trigger: changePlanTrigger,
    isMutating: changePlanIsMutating,
    error,
  } = useChangePaymentPlanMutation();
  const isMutating = isSubmittingForm || changePlanIsMutating;
  const submitDisabled =
    isMutating ||
    !userEmail ||
    !reasonsForUnsubscribing.size ||
    !currentOrg?.id;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    await trigger({
      [FormFieldIds.Email]: userEmail,
      [FormFieldIds.OrgId]: currentOrg.id,
      [FormFieldIds.Reasons]: Array.from(reasonsForUnsubscribing),
      [FormFieldIds.ExtraDetails]: extraDetails,
      [FormFieldIds.OtherReason]: otherReason,
    });
    await changePlanTrigger({
      json: {
        tier: isDeveloper ? 'free' : 'disabled',
      },
    });
    if (error) {
      createToast({
        title:
          'There was an error cancelling your subscription. Please try again.',
        description: error.message,
        type: 'error',
      });
      return;
    }
    onClose();
  };
  return (
    <div className="flex flex-col gap-4">
      <Text variant="body" className="text-secondary">
        Let us know why you're{' '}
        {isDeveloper ? 'removing your payment method' : 'cancelling'}.
      </Text>
      <div className="flex flex-col gap-2">
        <Text weight="medium" as="label">
          Reasons for unsubscribing{' '}
          <Text as="span" className="text-error">
            *
          </Text>
        </Text>
        {Object.values(ReasonsForUnsubscribing).map((reason) => (
          <Checkbox
            size="sm"
            key={reason}
            value={reason}
            label={<Text variant="sm">{reason}</Text>}
            checked={reasonsForUnsubscribing.has(reason)}
            onChange={(e) => {
              setReasonsForUnsubscribing((prev) => {
                const newSet = new Set(prev);
                if (e.target.checked) {
                  newSet.add(reason);
                } else {
                  newSet.delete(reason);
                }
                return newSet;
              });
            }}
          />
        ))}
        {reasonsForUnsubscribing.has(ReasonsForUnsubscribing.Other) && (
          <Input
            label="If other, please specify"
            value={otherReason}
            onChange={setOtherReason}
            size="sm"
            className="mt-2"
          />
        )}
      </div>

      <Textarea
        label="Any other details you would like to provide? We appreciate any feedback!"
        value={extraDetails}
        onChange={(value) => setExtraDetails(value)}
        rows={3}
        size="sm"
      />

      <div className="flex items-start gap-2 rounded-lg bg-brand-tertiary p-4">
        <AnnotationDotsIcon className="mt-1 size-4 shrink-0 text-brand-primary" />
        <Text>
          Before you go, we'd love to make things right. Please reach out to us
          at{' '}
          <a href="mailto:support@langchain.dev" className="underline">
            support@langchain.dev
          </a>{' '}
          to see if we can help resolve any issues you're having.
        </Text>
      </div>

      <div className="flex flex-row justify-end gap-2">
        <Button
          color="secondary"
          disabled={isMutating}
          size="md"
          onClick={onClose}
        >
          {isDeveloper ? 'Keep payment method' : 'Keep subscription'}
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          loading={isMutating}
          disabled={submitDisabled}
          size="md"
        >
          {isDeveloper ? 'Remove payment method' : 'Cancel subscription'}
        </Button>
      </div>
    </div>
  );
};

export const CancelSubscriptionModal = ({
  open,
  onClose: _onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { tier: currentTier } = useCurrentTier();
  const isDeveloper = currentTier === 'developer';

  const { data: currentOrganization } = useCurrentOrganizationBilling();
  const lastDayOfBillingPeriodFormatted =
    currentOrganization?.end_of_billing_period &&
    getDisplayDateFromEndOfBillingPeriod(
      currentOrganization.end_of_billing_period
    );

  const [showForm, setShowForm] = useState(false);

  const onClose = () => {
    _onClose();
    setShowForm(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        title={
          showForm
            ? "We're sorry to see you go"
            : `Are you sure you want to ${
                currentTier === 'developer'
                  ? 'remove your payment method'
                  : 'cancel your subscription'
              }?`
        }
        className="w-[500px]"
      >
        {showForm ? (
          <CancelFormStep isDeveloper={isDeveloper} onClose={onClose} />
        ) : (
          <CancelConfirmationStep
            isDeveloper={isDeveloper}
            lastDayOfBillingPeriodFormatted={lastDayOfBillingPeriodFormatted}
            onClose={onClose}
            handleContinue={() => setShowForm(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
